/**
 * Vendor "Business Profile" page (`/vendor/app/profile`).
 *
 * Aggregates the onboarding data the vendor already has and lets them edit the
 * business details + location in place (reusing the onboarding save endpoints).
 * Modules and KYC/submission status are read-only.
 *
 * Payout details ARE editable here, but only once every 30 days. Repointing where the money
 * goes is what an account takeover is for, so the change is deliberately slow, and admins are
 * notified of every one.
 */
import { useState, type FormEvent } from 'react';
import { CheckCircle2, Clock, Loader2, MapPin, Pencil, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { StatusPill } from '@/components/ui/status-pill';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { TextAreaField, TextField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import {
  use_draft,
  use_save_basic_info,
  use_saved_address,
  use_saved_basic_info,
} from '@/features/onboarding/hooks';
import {
  use_vendor_profile,
  use_update_vendor_profile,
} from '@/features/vendor-profile/hooks';
import {
  PayoutFields,
  EMPTY_PAYOUT_FORM,
  describe_payout_view,
  payout_payload,
  validate_payout,
  type PayoutForm,
} from '@/components/payout-fields';
import { use_create_payout, use_payout, use_replace_payout } from '@/features/vendor-profile/hooks';
import {
  BrandMediaCard,
  ServiceAreaCard,
  BookingConfigCard,
  DeliveryTimeCard,
  ContactSocialsCard,
} from './profile-deep-cards';
import { VetDetailsCard, CafeDetailsCard, RestaurantDetailsCard } from './profile-module-cards';
import { GalleryPhotosCard } from './profile-gallery-card';

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PINCODE_REGEX = /^[1-9][0-9]{5}$/;

function normalize_url(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** A labelled read-only row inside a view-mode card. */
function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm text-foreground">{value?.trim() ? value : '—'}</p>
    </div>
  );
}

export function VendorProfilePage() {
  const draft_q = use_draft();
  const basic_q = use_saved_basic_info({ enabled: !!draft_q.data });
  const address_q = use_saved_address({ enabled: !!draft_q.data });

  const draft = draft_q.data;
  const submitted = Boolean(draft?.submitted_at);
  const modules = draft?.module_types ?? [];
  const payout_state = draft?.payout_completed
    ? { tone: 'success' as const, label: 'Configured' }
    : draft?.payout_skipped
      ? { tone: 'neutral' as const, label: 'Skipped' }
      : { tone: 'warning' as const, label: 'Not set up' };

  const loading = draft_q.isLoading || basic_q.isLoading || address_q.isLoading;

  return (
    <div data-testid="vendor-profile" className="flex flex-col gap-6">
      <SectionHeader title="Business Profile" description="Your business details on file." />

      {loading ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-64 rounded-card" />
          <Skeleton className="h-64 rounded-card" />
        </div>
      ) : draft_q.isError ? (
        <ErrorState
          title="Couldn't load your profile"
          description="Please try again."
          action={
            <Button variant="secondary" size="sm" onClick={() => draft_q.refetch()}>
              Retry
            </Button>
          }
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <BrandMediaCard />
          <GalleryPhotosCard />
          <BusinessDetailsCard />
          <LocationCard />
          {modules.includes('VET') && <VetDetailsCard />}
          {modules.includes('CAFE') && <CafeDetailsCard />}
          {modules.includes('RESTAURANT') && <RestaurantDetailsCard />}
          <ServiceAreaCard />
          <BookingConfigCard />
          {(modules.includes('FOOD') || modules.includes('PHARMACY')) && <DeliveryTimeCard />}
          <ContactSocialsCard />

          <PayoutCard />

          {/* Account status — read-only */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Account status</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2" data-testid="profile-kyc-status">
                {submitted ? (
                  <StatusPill tone="success">
                    <CheckCircle2 className="h-4 w-4" /> Submitted for KYC review
                  </StatusPill>
                ) : (
                  <StatusPill tone="warning">
                    <Clock className="h-4 w-4" /> Not submitted yet
                  </StatusPill>
                )}
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Payout
                </p>
                <StatusPill tone={payout_state.tone} data-testid="profile-payout-status">
                  {payout_state.label}
                </StatusPill>
              </div>
              <p className="text-xs text-muted-foreground">
                Edits to business details may be re-reviewed by our team.
              </p>
            </CardContent>
          </Card>

          {/* Modules — read-only */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Modules</CardTitle>
            </CardHeader>
            <CardContent>
              {modules.length > 0 ? (
                <div className="flex flex-wrap gap-1.5" data-testid="profile-modules">
                  {modules.map((m) => (
                    <StatusPill key={m} tone="neutral">
                      {m}
                    </StatusPill>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No modules selected.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Business details (view / edit)
// ---------------------------------------------------------------------------

type BasicForm = {
  business_name: string;
  owner_name: string;
  email_for_business: string;
  website_url: string;
  description: string;
  gst_number: string;
  business_registration_number: string;
  is_24x7: boolean;
};

function BusinessDetailsCard() {
  const saved_q = use_saved_basic_info();
  const save_m = use_save_basic_info();
  const saved = saved_q.data;

  const [editing, set_editing] = useState(false);
  const [form, set_form] = useState<BasicForm | null>(null);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  function begin_edit() {
    if (!saved) return;
    set_form({
      business_name: saved.business_name,
      owner_name: saved.owner_name,
      email_for_business: saved.email_for_business,
      website_url: saved.website_url ?? '',
      description: saved.description ?? '',
      gst_number: saved.gst_number ?? '',
      business_registration_number: saved.business_registration_number ?? '',
      is_24x7: saved.is_24x7,
    });
    set_error(null);
    set_field_errors({});
    set_editing(true);
  }

  function update<K extends keyof BasicForm>(key: K, value: BasicForm[K]) {
    set_form((f) => (f ? { ...f, [key]: value } : f));
    set_field_errors((fe) => {
      if (!(key in fe)) return fe;
      const next = { ...fe };
      delete next[key];
      return next;
    });
  }

  function validate(f: BasicForm): Record<string, string> {
    const errors: Record<string, string> = {};
    if (f.business_name.trim().length < 2) errors.business_name = 'At least 2 characters.';
    if (f.owner_name.trim().length < 2) errors.owner_name = 'At least 2 characters.';
    if (!EMAIL_REGEX.test(f.email_for_business.trim()))
      errors.email_for_business = 'Enter a valid email.';
    const gst = f.gst_number.trim();
    if (gst !== '' && !GST_REGEX.test(gst))
      errors.gst_number = '15-character GSTIN, or leave blank.';
    return errors;
  }

  async function handle_submit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    set_error(null);
    const client_errors = validate(form);
    if (Object.keys(client_errors).length > 0) {
      set_field_errors(client_errors);
      set_error('Please fix the highlighted fields below.');
      return;
    }
    set_field_errors({});
    try {
      await save_m.mutateAsync({
        business_name: form.business_name.trim(),
        owner_name: form.owner_name.trim(),
        email_for_business: form.email_for_business.trim(),
        website_url: normalize_url(form.website_url) || undefined,
        description: form.description.trim() || undefined,
        gst_number: form.gst_number.trim() || undefined,
        business_registration_number: form.business_registration_number.trim() || undefined,
        is_24x7: form.is_24x7,
      });
      set_editing(false);
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save business details', { highlights_fields: true })
          : 'Could not save business details',
      );
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg">Business details</CardTitle>
        {!editing && saved && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={begin_edit}
            data-testid="edit-business"
          >
            <Pencil className="h-4 w-4" /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!editing || !form ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Business name" value={saved?.business_name ?? null} />
            <Field label="Owner name" value={saved?.owner_name ?? null} />
            <Field label="Business email" value={saved?.email_for_business ?? null} />
            <Field label="Website" value={saved?.website_url ?? null} />
            <Field label="GST number" value={saved?.gst_number ?? null} />
            <Field label="Registration no." value={saved?.business_registration_number ?? null} />
            <div className="sm:col-span-2">
              <Field label="Description" value={saved?.description ?? null} />
            </div>
            <Field label="Open 24×7" value={saved?.is_24x7 ? 'Yes' : 'No'} />
          </div>
        ) : (
          <form onSubmit={handle_submit} noValidate className="flex flex-col gap-5">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <TextField
                label="Business name"
                name="business_name"
                data-testid="field-business-name"
                value={form.business_name}
                onChange={(e) => update('business_name', e.target.value)}
                maxLength={120}
                error={field_errors.business_name}
              />
              <TextField
                label="Owner name"
                name="owner_name"
                value={form.owner_name}
                onChange={(e) => update('owner_name', e.target.value)}
                maxLength={120}
                error={field_errors.owner_name}
              />
              <TextField
                label="Business email"
                name="email_for_business"
                type="email"
                value={form.email_for_business}
                onChange={(e) => update('email_for_business', e.target.value)}
                maxLength={200}
                error={field_errors.email_for_business}
              />
              <TextField
                label="Website"
                name="website_url"
                optional
                value={form.website_url}
                onChange={(e) => update('website_url', e.target.value)}
                error={field_errors.website_url}
              />
              <TextField
                label="GST number"
                name="gst_number"
                optional
                value={form.gst_number}
                onChange={(e) => update('gst_number', e.target.value.toUpperCase().slice(0, 15))}
                error={field_errors.gst_number}
              />
              <TextField
                label="Registration no."
                name="business_registration_number"
                optional
                value={form.business_registration_number}
                onChange={(e) => update('business_registration_number', e.target.value)}
                maxLength={60}
                error={field_errors.business_registration_number}
              />
            </div>
            <TextAreaField
              label="Description"
              name="description"
              data-testid="field-description"
              optional
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              maxLength={2000}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_24x7}
                onChange={(e) => update('is_24x7', e.target.checked)}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              We operate 24×7
            </label>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                variant="gradient"
                disabled={save_m.isPending}
                data-testid="save-business"
              >
                {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => set_editing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Location (view / edit)
// ---------------------------------------------------------------------------

type AddressForm = {
  address: string;
  landmark: string;
  city: string;
  state: string;
  pincode: string;
};

/**
 * Where the vendor operates from, including the map pin.
 *
 * Writes through PATCH /vendor/profile, NOT the onboarding address step this card
 * used to post to. That step calls `require_editable_draft`, which 409s once an
 * application is submitted — so for every live vendor this card rendered inputs
 * and a Save button that always failed with "This application has been submitted
 * and can no longer be edited".
 *
 * The pin is editable here for the first time. It is collected at registration and
 * was then carried through untouched on every save, which mattered because
 * discovery matches on the pin and not on the printed address: a vendor who moved
 * cities stayed findable from the old one.
 */
function LocationCard() {
  const profile_q = use_vendor_profile();
  const save_m = use_update_vendor_profile();
  const saved = profile_q.data ?? null;

  const [editing, set_editing] = useState(false);
  const [form, set_form] = useState<AddressForm | null>(null);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});
  /**
   * The pin the vendor captured in THIS edit, or null if they did not touch it.
   *
   * Null means "omit the coordinates", which is what tells the server the pin was
   * not reviewed. Deliberately not seeded from the saved value: resending the old
   * coordinates is indistinguishable from re-pinning, and that is exactly the
   * signal the stale-pin warning depends on.
   */
  const [new_pin, set_new_pin] = useState<{ lat: string; lon: string } | null>(null);
  const [locating, set_locating] = useState(false);
  const [pin_stale, set_pin_stale] = useState(false);

  const has_pin = saved?.latitude != null && saved?.longitude != null;

  function begin_edit() {
    if (!saved) return;
    set_form({
      address: saved.address ?? '',
      landmark: saved.landmark ?? '',
      city: saved.city ?? '',
      state: saved.state ?? '',
      pincode: saved.pincode ?? '',
    });
    set_new_pin(null);
    set_error(null);
    set_pin_stale(false);
    set_field_errors({});
    set_editing(true);
  }

  /** Same browser geolocation the onboarding address step uses, same 7dp rounding. */
  function use_my_location() {
    set_error(null);
    if (!navigator.geolocation) {
      set_error('Your browser doesn’t support location. Your existing pin has been kept.');
      return;
    }
    set_locating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set_locating(false);
        set_new_pin({
          lat: pos.coords.latitude.toFixed(7),
          lon: pos.coords.longitude.toFixed(7),
        });
      },
      (geo_err) => {
        set_locating(false);
        set_error(
          geo_err.code === geo_err.PERMISSION_DENIED
            ? 'Location permission denied. Allow it in your browser, or save the address and update the pin later — we will flag it for review.'
            : 'Couldn’t get your location. Your existing pin has been kept.',
        );
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  function update<K extends keyof AddressForm>(key: K, value: AddressForm[K]) {
    set_form((f) => (f ? { ...f, [key]: value } : f));
    set_field_errors((fe) => {
      if (!(key in fe)) return fe;
      const next = { ...fe };
      delete next[key];
      return next;
    });
  }

  function validate(f: AddressForm): Record<string, string> {
    const errors: Record<string, string> = {};
    if (f.address.trim().length < 5) errors.address = 'At least 5 characters.';
    if (f.city.trim().length < 2) errors.city = 'Required.';
    if (f.state.trim().length < 2) errors.state = 'Required.';
    if (!PINCODE_REGEX.test(f.pincode.trim())) errors.pincode = '6-digit pincode.';
    return errors;
  }

  async function handle_submit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    set_error(null);
    const client_errors = validate(form);
    if (Object.keys(client_errors).length > 0) {
      set_field_errors(client_errors);
      set_error('Please fix the highlighted fields below.');
      return;
    }
    set_field_errors({});
    try {
      const updated = await save_m.mutateAsync({
        address: form.address.trim(),
        landmark: form.landmark.trim() || null,
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: form.pincode.trim(),
        // Sent only when re-captured in this edit. Omitting them keeps the old
        // pin and is what makes the server's stale-pin warning meaningful.
        ...(new_pin ? { latitude: Number(new_pin.lat), longitude: Number(new_pin.lon) } : {}),
      });
      // The server tells us the text moved but the pin did not. Say so here
      // rather than only telling an admin: the vendor is the one who can fix it,
      // and being findable at the wrong address costs them the booking.
      set_pin_stale(updated.location_needs_review === true);
      set_editing(false);
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save location', { highlights_fields: true })
          : 'Could not save location',
      );
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg">Location</CardTitle>
        {!editing && saved && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={begin_edit}
            data-testid="edit-location"
          >
            <Pencil className="h-4 w-4" /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!saved ? (
          <p className="text-sm text-muted-foreground">No address on file.</p>
        ) : !editing || !form ? (
          <div className="flex flex-col gap-4">
            {pin_stale && (
              <p
                className="rounded-card bg-warning/10 px-3 py-2 text-xs text-warning"
                data-testid="location-pin-stale"
              >
                Address saved, but your map pin still points at your old location. Customers are
                matched by the pin, so update it when you can — tap Edit, then “Update pin”.
              </p>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label="Address" value={saved.address} />
              </div>
              <Field label="Landmark" value={saved.landmark} />
              <Field label="City" value={saved.city} />
              <Field label="State" value={saved.state} />
              <Field label="Pincode" value={saved.pincode} />
              <div className="sm:col-span-2">
                <Field
                  label="Map pin"
                  value={
                    has_pin
                      ? `${saved.latitude}, ${saved.longitude}`
                      : 'Not set — customers cannot find you in nearby search'
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handle_submit} noValidate className="flex flex-col gap-5">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <TextAreaField
              label="Address"
              name="address"
              rows={2}
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
              error={field_errors.address}
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <TextField
                label="Landmark"
                name="landmark"
                optional
                value={form.landmark}
                onChange={(e) => update('landmark', e.target.value)}
              />
              <TextField
                label="City"
                name="city"
                value={form.city}
                onChange={(e) => update('city', e.target.value)}
                error={field_errors.city}
              />
              <TextField
                label="State"
                name="state"
                value={form.state}
                onChange={(e) => update('state', e.target.value)}
                error={field_errors.state}
              />
              <TextField
                label="Pincode"
                name="pincode"
                inputMode="numeric"
                value={form.pincode}
                onChange={(e) => update('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                error={field_errors.pincode}
              />
            </div>
            {/*
              The pin, and the honest statement of what happens if they skip it.

              Not a hard requirement: this form is five text fields and no map, and
              a vendor who has denied location permission would be as stuck as the
              409 used to leave them. So the text saves either way and the mismatch
              is flagged - to them here, and to admins server-side.
            */}
            <div className="flex flex-col gap-2 rounded-card bg-secondary/50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Map pin
              </p>
              <p className="text-sm" data-testid="location-pin-value">
                {new_pin
                  ? `${new_pin.lat}, ${new_pin.lon} (new)`
                  : has_pin
                    ? `${saved.latitude}, ${saved.longitude}`
                    : 'Not set'}
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="w-fit gap-2"
                onClick={use_my_location}
                disabled={locating}
                data-testid="location-use-my-location"
              >
                {locating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MapPin className="h-4 w-4" />
                )}
                {has_pin || new_pin ? 'Update pin' : 'Set pin'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Customers are matched by this pin, not by the address text. Stand at your business
                and tap the button. Leave it alone to keep your current pin.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                variant="gradient"
                disabled={save_m.isPending}
                data-testid="save-location"
              >
                {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => set_editing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Payout details, editable once every 30 days.
 *
 * `can_edit` and `next_editable_at` come from the server rather than being derived here: the
 * window is measured against the server clock, and a browser a day out would either hide an edit
 * the vendor is entitled to or offer one the server would refuse.
 */
function PayoutCard() {
  const payout_q = use_payout();
  const replace = use_replace_payout();
  // Separate from `replace`: a vendor with no account at all is completing the
  // onboarding step late, not moving their money. No cooldown is consumed.
  const create = use_create_payout();
  const [editing, set_editing] = useState(false);
  const [form, set_form] = useState<PayoutForm>(EMPTY_PAYOUT_FORM);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  const payout = payout_q.data ?? null;

  function update<K extends keyof PayoutForm>(key: K, value: PayoutForm[K]) {
    set_form((f) => ({ ...f, [key]: value }));
    set_field_errors((fe) => {
      if (!(key in fe)) return fe;
      const next = { ...fe };
      delete next[key];
      return next;
    });
  }

  function begin_edit() {
    // Start on the method they already use - most changes are a new account at the same kind of
    // destination, not a switch between bank and UPI.
    set_form({ ...EMPTY_PAYOUT_FORM, method: payout?.method ?? 'UPI' });
    set_field_errors({});
    set_error(null);
    set_editing(true);
  }

  async function handle_submit(e: FormEvent) {
    e.preventDefault();
    set_error(null);
    const client_errors = validate_payout(form);
    if (Object.keys(client_errors).length > 0) {
      set_field_errors(client_errors);
      set_error('Please fix the highlighted fields below.');
      return;
    }
    set_field_errors({});
    const save = payout ? replace : create;
    try {
      await save.mutateAsync(payout_payload(form));
      set_editing(false);
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save payout details', { highlights_fields: true })
          : 'Could not save payout details',
      );
    }
  }

  if (payout_q.isLoading) {
    return <Skeleton className="h-64 rounded-card" />;
  }

  return (
    <Card data-testid="profile-payout-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div>
          <CardTitle className="text-lg">Payout details</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">Where your earnings are sent.</p>
        </div>
        {!editing && payout?.can_edit && (
          <Button variant="secondary" size="sm" onClick={begin_edit} data-testid="payout-edit">
            <Pencil className="h-4 w-4" /> Change
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!payout ? (
          /*
           * No account at all. Only reachable for vendors who onboarded before the step became
           * mandatory - everyone after that had to provide one to submit. They cannot be paid, so
           * this is the loudest state on the page.
           */
          <div className="flex flex-col gap-3" data-testid="payout-missing">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
              <p className="font-medium">No payout details on file</p>
              <p className="mt-1 text-xs text-muted-foreground">
                We have nowhere to send your earnings. Your account was created before payout became
                a required step, so this one-off setup was never asked of you.
              </p>
            </div>
            {!editing && (
              <div>
                <Button
                  variant="gradient"
                  size="sm"
                  onClick={begin_edit}
                  data-testid="payout-setup"
                >
                  <Wallet className="h-4 w-4" /> Add payout details
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1" data-testid="payout-summary">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {payout.method === 'UPI' ? 'UPI ID' : 'Bank account'}
              </p>
              <p className="font-mono text-sm" data-testid="payout-destination">
                {describe_payout_view(payout)}
              </p>
              {payout.method === 'BANK_ACCOUNT' && payout.account_holder_name && (
                <p className="text-xs text-muted-foreground">{payout.account_holder_name}</p>
              )}
            </div>

            {/* Method-aware: a UPI ID has no "last four digits" to speak of. */}
            <p className="text-xs text-muted-foreground">
              {payout.method === 'UPI'
                ? 'Your UPI ID is stored encrypted and only ever shown masked - we cannot read it back, and neither can you.'
                : 'Only the last four digits are readable - the full account number is encrypted and cannot be shown again, even to us.'}
            </p>

            {payout.can_edit ? (
              <p className="text-xs text-muted-foreground" data-testid="payout-window-open">
                You can change these details now. After you do, the next change is allowed in{' '}
                {payout.cooldown_days} days.
              </p>
            ) : (
              <div
                className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs"
                data-testid="payout-locked"
              >
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  Changed on{' '}
                  <strong>
                    {payout.last_changed_at
                      ? new Date(payout.last_changed_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })
                      : 'recently'}
                  </strong>
                  . Payout details can be changed once every {payout.cooldown_days} days - next
                  change available on{' '}
                  <strong>
                    {payout.next_editable_at
                      ? new Date(payout.next_editable_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })
                      : 'later'}
                  </strong>
                  . If you need it changed sooner, contact support.
                </span>
              </div>
            )}
          </>
        )}

        {editing && (
          <form
            onSubmit={handle_submit}
            noValidate
            className="flex flex-col gap-4 border-t border-border pt-4"
          >
            {error && (
              <div
                className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="payout-error"
              >
                {error}
              </div>
            )}
            {/* A first-time setup consumes no cooldown and is not a "change" —
                telling a vendor otherwise would make them hesitate over a step we
                just asked them to complete. */}
            {payout ? (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs">
                This replaces your payout destination and uses up your change for the next{' '}
                {payout.cooldown_days} days. Our team is notified of every change.
              </div>
            ) : (
              <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                Your account number is encrypted and only ever shown to you as the last four digits.
                You can change these later if you need to.
              </div>
            )}
            <PayoutFields form={form} field_errors={field_errors} on_change={update} />
            <div className="flex gap-2">
              <Button
                type="submit"
                variant="gradient"
                size="sm"
                disabled={replace.isPending || create.isPending}
                data-testid="payout-save"
              >
                {(replace.isPending || create.isPending) && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {payout ? 'Save new details' : 'Save payout details'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => set_editing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
